import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { isUndefined } from '@src/utils/type';
import { useAccount } from '@src/contexts';
import { useEnv } from './env-context';
import { useLocation } from 'react-router-dom';
import { DURATION_1_HR, DURATION_1_MIN, DURATION_5_SEC } from '@src/constants';
import { useGetIdIsActive, useVeriffStatus } from '@src/hooks/queries/auth';
import { getDeviceUuid, getNavigator } from '@src/utils/browserNavigator';
import { useGetBusinessVerificationStatus } from '@src/hooks/queries/user';

export type DashboardContext = {
  isSendBtnClicked: boolean;
  handleClickTrue: () => void;
  handleClickFalse: () => void;
  isUserVerified: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  veriffData: any;
  veriffUrl: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleVeriff: () => Promise<any>;
  handleVeriffOpenTab: () => Promise<void>;
  handleVeriffSameTab: () => Promise<void>;
  businessStatus: 0 | 2 | 1 | 3 | undefined;
  shouldBusinessMakeTransactions: boolean;
  isBusinessVerified: boolean;
  isVeriffLoading: boolean;
  isIdExpired: boolean;
};

// this is a context created to trigger some functions across the dashboard
const Context = createContext<DashboardContext | undefined>(undefined);

interface DashboardProviderProps {
  children?: ReactNode;
}

const DashboardProvider: React.FC<DashboardProviderProps> = ({ children }) => {
  const [isSendBtnClicked, setIsSendBtnClicked] = useState(false);
  const location = useLocation();
  const handleClickTrue = useCallback(() => {
    setIsSendBtnClicked(true);
  }, []);
  const handleClickFalse = useCallback(() => {
    setIsSendBtnClicked(false);
  }, []);
  const [isUserVerified, setIsUserVerified] = useState(false);
  const [businessStatus, setBusinessStatus] = useState<0 | 1 | 2 | 3>();
  const envData = useEnv();
  const { user, veriffSessions, isLoggedIn } = useAccount();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [veriffData, setVeriffData] = useState<any>({});
  const [veriffUrl, setVeriffUrl] = useState('');

  const { IS_PROD, IS_PILOT } = useEnv();

  const { data: idIsActiveData } = useGetIdIsActive({
    enabled: isLoggedIn
  });

  const isIdExpired = !!idIsActiveData && idIsActiveData?.Data === false;

  // used to check if the user is verified by veriff
  const veriff = useVeriffStatus(
    { email: user?.email || '' },
    {
      enabled: isLoggedIn && user?.userOnboardingStatus === 2,
      staleTime: isUserVerified || !isLoggedIn ? DURATION_1_HR : DURATION_5_SEC,
      refetchInterval: isUserVerified || !isLoggedIn ? undefined : DURATION_5_SEC
    }
  );

  const {
    isLoading: businessVerificationLoading,
    data: businessVerificationData,
    isError: businessVerificationError
  } = useGetBusinessVerificationStatus({
    enabled: isLoggedIn && user?.userOnboardingStatus === 2 && businessStatus !== 2,
    staleTime: DURATION_1_MIN,
    refetchInterval: DURATION_1_MIN
  });

  const navigatorDetail = getNavigator();
  // payload for the veriff session
  const payload = useMemo(() => {
    const data = {
      callbackUrl: `${envData.APP_DEPLOY_URL}/veriff`,
      person: {
        firstName: `${user?.firstName}`,
        lastName: `${user?.lastName}`,
        email: `${user?.email}`,
        phoneNumber: ''
      },
      // document: {
      //   type: "intl-passport",
      //   number: "B50045471",
      //   country: "NG",
      // },
      platform: 'mobile',
      device: {
        uuId: `${getDeviceUuid()}`,
        name: `${navigatorDetail.browserName}`,
        osName: `${navigatorDetail.osName}`,
        osVersion: `${navigatorDetail.osVersion}`
      }
    };
    return data;
  }, [
    envData.APP_DEPLOY_URL,
    navigatorDetail.browserName,
    navigatorDetail.osName,
    navigatorDetail.osVersion,
    user?.email,
    user?.firstName,
    user?.lastName
  ]);
  // function to handle the veriff session
  const handleVeriff = useCallback(async () => {
    const ver = await veriffSessions(payload);
    setVeriffData(ver);
    if (ver.success) {
      setVeriffUrl(ver?.data?.session?.url);
    }
    return ver;
  }, [payload, veriffSessions]);

  const handleVeriffOpenTab = useCallback(async () => {
    const newWindow = window.open('');
    newWindow?.document.write('Loading...'); // to let the user know that an action is in process
    const veriff = await handleVeriff();

    if (veriff && (IS_PROD || IS_PILOT)) {
      const url = veriff?.data?.session?.url;
      if (newWindow) {
        newWindow.location.href = url;
      }
    }

    if (veriff && newWindow && !(IS_PROD || IS_PILOT)) {
      // newWindow?.document.write('Verified...'); // to let the user know that an action is in process
      newWindow.location.href = veriff?.data?.session?.url;

      // setTimeout(() => {
      //   newWindow?.close();
      // }, DURATION_5_SEC);
    }
  }, [handleVeriff]);
  const handleVeriffSameTab = useCallback(async () => {
    const veriff = await handleVeriff();
    if (veriff) {
      const url = veriff?.data?.session?.url;
      window.location.href = url;
    }
  }, [handleVeriff]);
  // effect to update the verification status and url
  useEffect(() => {
    if (!veriff.isLoading && !veriff.isError) {
      if (veriff.data === null) {
        setIsUserVerified(false);
        // if (user) {
        //   handleVeriff();
        // }
      } else {
        setIsUserVerified(true);
      }
    }
  }, [veriff.data, veriff.isLoading, veriff.isError]);

  useEffect(() => {
    if (!businessVerificationLoading && !businessVerificationError) {
      if (businessVerificationData) {
        setBusinessStatus(businessVerificationData?.status);
      }
    }
  }, [businessVerificationData, businessVerificationLoading, businessVerificationError]);

  // effect to trigger the veriff request
  // useEffect(() => {
  //   if (user) {
  //     handleVeriff();
  //   }
  // }, [user, handleVeriff]);
  // this is used to clean up the localstorage just incase the user didnt click the button in the veriff page in the other tab.
  useEffect(() => {
    // used to detect if the page is on focus
    if (document.visibilityState === 'visible') {
      setTimeout(() => {
        localStorage.removeItem('SprintData');
      }, 60 * 1000 * 60 * 24); // one day
    }
  }, [location.pathname]);

  const isBusinessVerified = businessStatus === 2;
  const shouldBusinessMakeTransactions = isBusinessVerified && isUserVerified && !isIdExpired;

  const value = useMemo<DashboardContext>(
    () => ({
      isSendBtnClicked,
      handleClickTrue,
      handleClickFalse,
      isUserVerified,
      veriffData,
      veriffUrl,
      handleVeriff,
      handleVeriffOpenTab,
      handleVeriffSameTab,
      businessStatus,
      isBusinessVerified,
      shouldBusinessMakeTransactions,
      isVeriffLoading: veriff.isLoading,
      isIdExpired
    }),
    [
      isSendBtnClicked,
      handleClickTrue,
      handleClickFalse,
      isUserVerified,
      veriffData,
      veriffUrl,
      handleVeriff,
      handleVeriffOpenTab,
      handleVeriffSameTab,
      businessStatus,
      isBusinessVerified,
      shouldBusinessMakeTransactions,
      veriff.isLoading,
      isIdExpired
    ]
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
};

const useDashboardContext = () => {
  const context = useContext(Context);

  if (isUndefined(context)) {
    throw new Error(`useDashboardContext must be used within a DashboardProvider`);
  }
  return context;
};

export { DashboardProvider, useDashboardContext };
